import * as React from 'react';

import { TrainedModelConfig } from '@nexxt/common/types';
import {
    UilTrashAlt,
    UilAngleUp,
    UilAngleDown
} from '@iconscout/react-unicons';
import Toggle from '../../../../common/Toggle';

interface TrainedModelEntryProps {
    langId: string;
    label: string;
    disabled: boolean;
    trainedModel: TrainedModelConfig;
    onTrainedModelChange: (
        trainedModel: TrainedModelConfig,
        langId: string
    ) => void;
    baseQuestion: string;
}

const TrainedModelEntry = ({
    langId,
    label,
    disabled,
    trainedModel,
    onTrainedModelChange,
    baseQuestion
}: TrainedModelEntryProps): JSX.Element => {
    const [expanded, setExpanded] = React.useState<boolean>(true);
    const [useCustomQuestion, setUseCustomQuestion] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        const differentPrimeQuestions = trainedModel.training_examples.filter(
            (model) => model.prime_question !== baseQuestion
        );

        if (differentPrimeQuestions.length > 0) {
            setUseCustomQuestion(true);
        }
    }, []);

    const handleUseCustomQuestionToggle = (value) => {
        if (!value) {
            const resetTrainingExamples = trainedModel.training_examples.map(
                (example) => ({ ...example, prime_question: baseQuestion })
            );
            onTrainedModelChange(
                { ...trainedModel, training_examples: resetTrainingExamples },
                langId
            );
        }
        setUseCustomQuestion(value);
    };

    const handleAddExample = () => {
        onTrainedModelChange(
            {
                ...trainedModel,
                training_examples: [
                    ...trainedModel.training_examples,
                    {
                        prime_question: baseQuestion,
                        prime_response: '',
                        probe_question: ''
                    }
                ]
            },
            langId
        );
    };
    const handleDeleteExample = (index: number) => {
        const newTrainedModel = { ...trainedModel };
        newTrainedModel.training_examples.splice(index, 1);

        onTrainedModelChange(newTrainedModel, langId);
    };

    const handleExamples = (index: number, keyName: string, value: string) => {
        const examples = trainedModel.training_examples;
        const currentExample = examples?.[index];
        const newValue = { ...currentExample, [keyName]: value };
        let newTrainedModelExamples;

        if (currentExample) {
            newTrainedModelExamples = [
                ...examples.slice(0, index),
                newValue,
                ...examples.slice(index + 1)
            ];
        } else {
            newTrainedModelExamples = [...examples, newValue];
        }

        const newTrainedModel = {
            ...trainedModel,
            training_examples: newTrainedModelExamples
        };

        onTrainedModelChange(newTrainedModel, langId);
    };
    return (
        <div className="w-full border rounded border-gray-500 p-4 mt-2">
            <div className="font-bold text-lg flex justify-between w-full">
                {label}
                <span className="flex gap-4">
                    <span onClick={() => setExpanded((toggle) => !toggle)}>
                        {expanded ? (
                            <UilAngleUp size={20} />
                        ) : (
                            <UilAngleDown size={20} />
                        )}
                    </span>
                </span>
            </div>
            {expanded ? (
                <div className="w-full flex flex-col mt-4">
                    <div className="flex gap-4 mb-4 justify-between items-center">
                        <div
                            className="font-bold text-blue hover:text-blue/80 cursor-pointer text-uppercase"
                            onClick={() => {
                                if (
                                    trainedModel.training_examples.length < 10
                                ) {
                                    handleAddExample();
                                } else {
                                    alert(
                                        "You've hit the maximum number of examples. You may only enter between 3 - 10 training examples. To provide a larger data set for further training, please contact us."
                                    );
                                }
                            }}
                        >
                            Add a new Example
                        </div>
                        <div>
                            <Toggle
                                label="Use Different Prime Questions"
                                onChange={(value) =>
                                    handleUseCustomQuestionToggle(value)
                                }
                                checked={useCustomQuestion}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    {!useCustomQuestion && (
                        <div>
                            <div className="form-group flex-1">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Question Text
                                </label>
                                <div className="smartProbe--target--examples">
                                    <input
                                        className="form-group__control"
                                        disabled={disabled}
                                        placeholder={`English Question Text Example`}
                                        onChange={(e) => {}}
                                        value={
                                            trainedModel.training_examples?.[0]
                                                .prime_question
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex gap-4">
                        {useCustomQuestion && (
                            <div className="form-group flex-1">
                                <label className="form-group__label form-group-header form-group__label--block">
                                    Prime Question
                                </label>
                                <div className="smartProbe--target--examples">
                                    {Array.from(
                                        Array(
                                            trainedModel.training_examples
                                                .length
                                        ).keys()
                                    ).map((index) => {
                                        return (
                                            <input
                                                key={`prime-question-${index}`}
                                                className="form-group__control"
                                                disabled={disabled}
                                                placeholder={`Prime Question Example ${
                                                    index + 1
                                                }`}
                                                onChange={(e) =>
                                                    handleExamples(
                                                        index,
                                                        'prime_question',
                                                        e.target.value
                                                    )
                                                }
                                                value={
                                                    trainedModel
                                                        .training_examples?.[
                                                        index
                                                    ].prime_question
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className="form-group flex-1">
                            <label className="form-group__label form-group-header form-group__label--block">
                                Prime Response
                            </label>
                            <div className="smartProbe--target--examples">
                                {Array.from(
                                    Array(
                                        trainedModel.training_examples.length
                                    ).keys()
                                ).map((index) => {
                                    return (
                                        <input
                                            key={`prime-response-${index}`}
                                            className="form-group__control"
                                            disabled={disabled}
                                            placeholder={`Prime Response Example ${
                                                index + 1
                                            }`}
                                            onChange={(e) =>
                                                handleExamples(
                                                    index,
                                                    'prime_response',
                                                    e.target.value
                                                )
                                            }
                                            value={
                                                trainedModel
                                                    .training_examples?.[index]
                                                    .prime_response
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-group__label form-group-header form-group__label--block">
                                Probe Question
                            </label>
                            <div className="smartProbe--target--examples">
                                {Array.from(
                                    Array(
                                        trainedModel.training_examples.length
                                    ).keys()
                                ).map((index) => {
                                    return (
                                        <input
                                            key={`probe-question-${index}`}
                                            className="form-group__control"
                                            disabled={disabled}
                                            placeholder={`Probe Question Example ${
                                                index + 1
                                            }`}
                                            onChange={(e) =>
                                                handleExamples(
                                                    index,
                                                    'probe_question',
                                                    e.target.value
                                                )
                                            }
                                            value={
                                                trainedModel
                                                    .training_examples?.[index]
                                                    .probe_question
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        {trainedModel.training_examples.length > 3 && (
                            <div className="form-group">
                                <label className="form-group__label form-group-header form-group__label--block h-[18px]">
                                    {` `}
                                </label>
                                <div className="smartProbe--target--examples">
                                    {Array.from(
                                        Array(
                                            trainedModel.training_examples
                                                .length
                                        ).keys()
                                    ).map((index) => {
                                        return index > 2 ? (
                                            <div
                                                key={`delete-${index}`}
                                                className={`w-8 h-[35px] flex items-center justify-center cursor-pointer`}
                                                onClick={() =>
                                                    handleDeleteExample(index)
                                                }
                                            >
                                                <UilTrashAlt size={14} />
                                            </div>
                                        ) : (
                                            <div className="w-8 h-[35px]"></div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default TrainedModelEntry;
